<template>
  <div class="score score--das-28 rythm-v">
    <h4>Score d’activité de la polyarthrite rhumatoïde DAS 28<br /><small>(Disease Activity Score)</small></h4>

    <div>
      <radio-toggle class="radio-group--inline" id="score_das28_done" name="score_das28_done" label="Score réalisé ?" v-model="scoreDone" @input="resetScore"></radio-toggle>
    </div>

    <div class="rythm-v-large" v-if="parseInt(scoreDone)">
      <section class="rythm-v">
        <h5>Articulations douloureuses</h5>
        <div class="rythm-v">
          <p>
            Nombre d'articulations douloureuses : <strong>{{ painfulJointsTotal }}</strong>
          </p>
          <DasSelect :key="`das_painfuljoints_${session.id}`" set="das" v-model="painfulJoints.checkedDots" @input="emitScore"></DasSelect>
        </div>
      </section>

      <section class="rythm-v">
        <h5>Articulations gonflées</h5>
        <div class="rythm-v">
          <p>
            Nombre d'articulations gonflées : <strong>{{ swollenJointsTotal }}</strong>
          </p>
          <DasSelect :key="`das_swollenjoints_${session.id}`" set="das" v-model="swollenJoints.checkedDots" @input="emitScore"></DasSelect>
        </div>
      </section>

      <div class="flex-row flex-gap-large">
        <div class="flex-item--50">
          <div class="input-group--inline required">
            <label for="das_28_vs">VS (mm/h) :</label>
            <input type="number" name="das_28_vs" id="das_28_vs" v-model="vs" v-bind="$attrs" step=".01" required @input="emitScore" />
          </div>
        </div>

        <div class="flex-item--50">
          <div class="input-group--inline required">
            <label for="das_28_crp">CRP(mg/l) :</label>
            <input type="number" name="das_28_crp" id="das_28_crp" v-model="crp" v-bind="$attrs" step=".01" required @input="emitScore" />
          </div>
        </div>

        <!-- Display results -->
        <div class="alert-success flex-item--50">
          <ul>
            <li>
              <span class="label">PGA (EVA Activité Patient x 10) :</span> <strong v-if="!isNaN(PGA) && PGA !== ''">{{ PGA }}</strong> <strong class="text-danger" v-else>DM</strong>
            </li>
            <li>
              <span class="label">Articulations douloureuses :</span> <strong>{{ painfulJointsTotal }}</strong>
            </li>
            <li>
              <span class="label">Articulations gonflées :</span> <strong>{{ swollenJointsTotal }}</strong>
            </li>
            <li>
              <span class="label">DAS 28 VS : </span>
              <template v-if="scoreVS">
                <strong :class="scoreVS == 'DM' ? 'text-danger' : ''">{{ scoreVS }}</strong
                >&nbsp; <span v-if="das28vsSeverity">({{ das28vsSeverity.label }})</span></template
              >
            </li>
            <li>
              <span class="label">DAS 28 CRP : </span>
              <template v-if="scoreCRP"
                ><strong :class="scoreCRP == 'DM' ? 'text-danger' : ''">{{ scoreCRP }}</strong></template
              >
            </li>
          </ul>
        </div>

        <div class="alert-info flex-item--50">
          <div>
            <p class="label">Interprétation du score DAS 28 VS :</p>
            <ul>
              <li>DAS 28 &lt; 2,6 : Rémission</li>
              <li>DAS 28 &gt;= 2,6 et &lt;= 3,2 : Activité de la maladie faible</li>
              <li>DAS 28 &gt; 3,2 et &lt;= 5,1 : Activité de la maladie modérée</li>
              <li>DAS 28 > 5,1 : Activité de la maladie élevée</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DasSelect from "./DasSelect.vue";
import radioToggle from "@/components/radioToggle.vue";

export default {
  name: "ScoreDas28",
  inheritAttrs: false,
  components: {
    DasSelect,
    radioToggle,
  },
  props: ["value", "session"],

  data() {
    return {
      scoreDone: "",
      painfulJoints: {
        checkedDots: [],
      },
      swollenJoints: {
        checkedDots: [],
      },
      vs: 0,
      crp: 0,
    };
  },

  computed: {
    das28vsSeverity() {
      // Interprétation du score DAS 28 VS

      if (this.scoreVS < 2.6) {
        return {
          label: "rémission",
          level: 1,
        };
      } else if (this.scoreVS >= 2.6 && this.scoreVS <= 3.2) {
        return {
          label: "activité faible",
          level: 2,
        };
      } else if (this.scoreVS > 3.2 && this.scoreVS <= 5.1) {
        return {
          label: "activité modérée",
          level: 3,
        };
      } else if (this.scoreVS > 5.1) {
        return {
          label: "activité élevée",
          level: 4,
        };
      } else {
        return "";
      }
    },

    painfulJointsTotal() {
      if (!this?.painfulJoints?.checkedDots.length) return 0;
      return this.painfulJoints.checkedDots.length;
    },

    swollenJointsTotal() {
      if (!this?.swollenJoints?.checkedDots.length) return 0;
      return this.swollenJoints.checkedDots.length;
    },

    PGA() {
      // EVA Activité patient
      if (!parseInt(this.session?.clinical_data?.eva?.activity_patient_done) || this.session?.clinical_data?.eva?.activity_patient === "") {
        console.log("Missing activity_patient_done or activity_patient ");

        return "";
      }
      return parseFloat(this.session.clinical_data.eva.activity_patient) * 10;
    },

    scoreVS() {
      return this.calculateDAS28Score("vs", this.painfulJointsTotal, this.swollenJointsTotal, this.vs);
    },

    scoreCRP() {
      return this.calculateDAS28Score("crp", this.painfulJointsTotal, this.swollenJointsTotal, this.crp);
    },

    results() {
      return {
        scoreDone: this.scoreDone.toString(),
        score: `${this.scoreVS},${this.scoreCRP}`, // raw score always as string
        scoreRendered: `DAS 28 VS : ${this.scoreVS}, DAS 28 CRP : ${this.scoreCRP}`, // formatted score, ex. with unit 24%, 12mm, …
        values: {
          pga: this.PGA,
          painfulJoints: this.painfulJoints.checkedDots,
          swollenJoints: this.swollenJoints.checkedDots,
          painfulJointsTotal: this.painfulJointsTotal,
          swollenJointsTotal: this.swollenJointsTotal,
          vs: this.vs,
          crp: this.crp,
          score_vs: this.scoreVS,
          score_crp: this.scoreCRP,
        },
        details: null,
        detailsRendered: "",
      };
    },
  },

  methods: {
    resetScore() {
      if (!parseInt(this.scoreDone)) {
        this.painfulJoints = Object.assign({}, { checkedDots: [] });
        this.swollenJoints = Object.assign({}, { checkedDots: [] });
        this.vs = "";
        this.crp = "";
      }
      this.emitScore();
    },

    /**
     * DAS = 0,55 x(Indice articulaire : 28) + 0,284 x(synovites : 28) + 0,33 x log VS (ou CRP)
     * Non ajouté au score : (0,0142 x appréciation globale du patient)
     *
     * FIX: DAS28 VS : `0,55 x RACINE(NAD) + 0,284 x RACINE(NAG) + 0,7 x LN(VS) + 0,0142 x PGA`
     * FIX: DAS28 CRP : `0,55 x RACINE(NAD) + 0,284 x RACINE(NAG) + 0,36 x LN(CRP+1) + 0,0142 x PGA + 0.96`
     *
     * jointIndex: this.painfulJointsTotal
     * synovites:  this.swollenJointsTotal
     */
    calculateDAS28Score(mode, painfulJointsTotal, swollenJointsTotal, vs_or_crp) {
      // EVA Activité patient
      if (isNaN(this.PGA) || this.PGA === "") {
        console.info("calculateDAS28Score: Missing EVA Activity Patient (PGA)");
        return "DM";
      }

      let das28Score = 0;
      painfulJointsTotal = parseInt(painfulJointsTotal);
      swollenJointsTotal = parseInt(swollenJointsTotal);
      vs_or_crp = parseInt(vs_or_crp);
      const PGA = parseInt(this.PGA);

      console.log("PGA", this.PGA);
      console.log("painfulJointsTotal", painfulJointsTotal);
      console.log("swollenJointsTotal", swollenJointsTotal);

      // Check if the input values are valid
      if (!mode || isNaN(painfulJointsTotal) || isNaN(swollenJointsTotal) || isNaN(vs_or_crp) || vs_or_crp <= 0) {
        console.info("calculateDAS28Score: Missing data");
        return "DM";
      }

      // NOTE: Les formules de ce site sont différentes http://medicalcul.free.fr/das28.html
      // "em" = "PGA"
      // DAS (VS) = 0,56 x √ad + 0,28 x √ag + 0,7 x ln(VS) + 0,014 x em
      // DAS (CRP) = 0,56 x √ad + 0,28 x √ag + 0,36 x ln(CRP + 1) + 0,014 x em + 0,96

      if (mode === "vs") {
        // DAS28 VS : `0,55 x RACINE(NAD) + 0,284 x RACINE(NAG) + 0,7 x LN(VS) + 0,0142 x PGA`
        const logVS = Math.log(vs_or_crp);
        console.log("vs_or_crp", vs_or_crp);
        console.log("logVS", logVS);

        console.log("Math.sqrt(painfulJointsTotal)", Math.sqrt(painfulJointsTotal));
        console.log("Math.sqrt(swollenJointsTotal)", Math.sqrt(swollenJointsTotal));

        // DAS28 VS #1
        // das28Score = 0.55 * Math.sqrt(painfulJointsTotal) + 0.284 * Math.sqrt(swollenJointsTotal) + 0.7 * logVS + 0.0142 * PGA;

        // DAS28 VS #2
        // DAS (VS) = 0,56 x √ad + 0,28 x √ag + 0,7 x ln(VS) + 0,014 x em
        das28Score = 0.56 * Math.sqrt(painfulJointsTotal) + 0.28 * Math.sqrt(swollenJointsTotal) + 0.7 * logVS + 0.014 * PGA;
        console.info("das28Score VS", das28Score);

        //
      } else if (mode === "crp") {
        // DAS28 CRP : `0,55 x RACINE(NAD) + 0,284 x RACINE(NAG) + 0,36 x LN(CRP+1) + 0,0142 x PGA + 0.96`
        const logCRP = Math.log(vs_or_crp + 1);

        console.log("vs_or_crp", vs_or_crp);
        console.log("logCRP", logCRP);

        console.log("Math.sqrt(painfulJointsTotal)", Math.sqrt(painfulJointsTotal));
        console.log("Math.sqrt(swollenJointsTotal)", Math.sqrt(swollenJointsTotal));

        // DAS28 CRP #1
        // das28Score = 0.55 * Math.sqrt(painfulJointsTotal) + 0.284 * Math.sqrt(swollenJointsTotal) + 0.36 * logCRP + 0.0142 * PGA + 0.96;

        // DAS28 CRP #2
        // DAS (CRP) = 0,56 x √ad + 0,28 x √ag + 0,36 x ln(CRP + 1) + 0,014 x em + 0,96
        das28Score = 0.56 * Math.sqrt(painfulJointsTotal) + 0.28 * Math.sqrt(swollenJointsTotal) + 0.36 * logCRP + 0.014 * PGA + 0.96;
        console.info("das28Score CRP", das28Score);
      }

      // Round the result to two decimal places
      return das28Score.toFixed(2);
    },

    emitScore() {
      this.$emit("input", this.results);
    },
  },

  watch: {
    // Init data values from component props ("values")
    value: {
      immediate: true,
      handler: function(data) {
        this.scoreDone = data?.scoreDone || "";
        this.painfulJoints.checkedDots = data?.values?.painfulJoints || [];
        this.swollenJoints.checkedDots = data?.values?.swollenJoints || [];
        this.vs = data?.values?.vs || 0;
        this.crp = data?.values?.crp || 0;
      },
    },
  },
};
</script>
